/**
 * Spend ledger vocabulary (token efficiency): every model call (and every Workers AI call that costs money, and the
 * cloud computer's light sleep: « computer ») is recorded
 * under one category, per local day, with its tokens and its cost. Shared by the Worker (lib/usage.ts records, GET
 * /api/usage/breakdown reads) and the web app (the « Dépenses » block of /activite). Images and pages the agent reads
 * are counted with the call that reads them (chat, mission, lanes): no model call does vision alone.
 */

export const USAGE_CATEGORIES = [
  "chat",
  "onboarding",
  "proactive",
  "watch_screening",
  "watch_judge",
  "digest",
  "pulse_summary",
  "memory_extraction",
  "memory_consolidation",
  "mission",
  "hermes",
  "computer",
  "subagent",
  "lanes",
  "avatar",
  "whisper",
  "other",
] as const;
export type UsageCategory = (typeof USAGE_CATEGORIES)[number];

/** French labels of the « Dépenses » block. */
export const USAGE_CATEGORY_LABELS: Record<UsageCategory, string> = {
  chat: "Conversation",
  onboarding: "Premiers échanges",
  proactive: "Messages spontanés",
  watch_screening: "Veilles (tri)",
  watch_judge: "Veilles (jugement)",
  digest: "Résumés",
  pulse_summary: "Résumé de Pulse",
  memory_extraction: "Mémoire (extraction)",
  memory_consolidation: "Mémoire (nuit)",
  mission: "Missions",
  hermes: "Hermès (ancien exécuteur)",
  computer: "Ordinateur cloud (veille)",
  subagent: "Sous-agents",
  lanes: "Tâches en parallèle",
  avatar: "Avatar",
  whisper: "Notes vocales",
  other: "Autre",
};

export function isUsageCategory(value: unknown): value is UsageCategory {
  return typeof value === "string" && (USAGE_CATEGORIES as readonly string[]).includes(value);
}

/** One category's totals over a period. */
export interface UsageCategoryTotals {
  category: UsageCategory;
  usd: number;
  calls: number;
  inputTokens: number;
  cacheWriteTokens: number;
  cacheReadTokens: number;
  outputTokens: number;
}

/** GET /api/usage/breakdown */
export interface UsageBreakdown {
  /** Local day of the user (YYYY-MM-DD). */
  today: string;
  todayUsd: number;
  last7Usd: number;
  /** DAILY_TARGET_USD: the day's cost objective, shown for reference (it never slows anything down). */
  dailyTargetUsd: number;
  /** By category, most expensive first. */
  todayByCategory: UsageCategoryTotals[];
  last7ByCategory: UsageCategoryTotals[];
  /** The last 7 local days, oldest first (days without spend included, at 0). */
  days: { day: string; usd: number }[];
  /** Share of the prompt read from the cache today (cache reads / all prompt tokens), 0..1. */
  todayCacheHitRatio: number;
}
